/**
 * Diligence domain: shared contract helpers, safe evidence links and the
 * personal GDV value shown beside the saved valuation evidence.
 *
 * The saved diligence views live in their own modules (areaPotential.ts,
 * marketOfferings.ts, schools.ts); this file holds what they share and the one
 * write the old diligence panels had (your GDV, via personal settings).
 */
import { ApiError } from './http'

export type JsonRecord = Record<string, unknown>

/** A thrower bound to one contract code, so a mismatch names its view. */
export function contract(code: string) {
  const fail = (message: string): never => { throw new ApiError(502, code, message) }
  const record = (value: unknown, name: string): JsonRecord => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) fail(`${name} must be an object`)
    return value as JsonRecord
  }
  return {
    fail,
    record,
    nullableRecord: (value: unknown, name: string): JsonRecord | null => value === null ? null : record(value, name),
    string: (value: unknown, name: string): string => { if (typeof value !== 'string') fail(`${name} must be a string`); return value as string },
    nullableString: (value: unknown, name: string): string | null => {
      if (value === null || value === undefined) return null
      if (typeof value !== 'string') fail(`${name} must be a string`)
      return value as string
    },
    number: (value: unknown, name: string): number => { if (typeof value !== 'number' || !Number.isFinite(value)) fail(`${name} must be a finite number`); return value as number },
    nullableNumber: (value: unknown, name: string): number | null => {
      if (value === null) return null
      if (typeof value !== 'number' || !Number.isFinite(value)) fail(`${name} must be a finite number or null`)
      return value as number
    },
    boolean: (value: unknown, name: string): boolean => { if (typeof value !== 'boolean') fail(`${name} must be a boolean`); return value as boolean },
    strings: (value: unknown, name: string): string[] => {
      if (!Array.isArray(value) || value.some(item => typeof item !== 'string')) fail(`${name} must be a string array`)
      return value as string[]
    },
    literal: <T extends string>(value: unknown, allowed: readonly T[], name: string): T => {
      if (typeof value !== 'string' || !allowed.includes(value as T)) fail(`${name} has an unsupported value`)
      return value as T
    },
    count: (value: unknown, name: string, nullable = false): number | null => {
      if (nullable && value === null) return null
      if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) fail(`${name} must be a nonnegative integer`)
      return value as number
    },
  }
}

const CONTROL = /[\u0000-\u001f\u007f]/

/** Evidence links are retained source URLs; only plain http(s) without credentials is followed. */
export function safeExternalHttpUrl(value: string | null | undefined): string | null {
  if (!value || value !== value.trim() || CONTROL.test(value)) return null
  try {
    const url = new URL(value)
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null
    return url.toString()
  } catch {
    return null
  }
}

/** Exact pounds text ("1234.5") to integer pence; anything else is null. */
export function poundsToPence(value: string): number | null {
  if (!/^\d+(?:\.\d{1,2})?$/.test(value.trim())) return null
  const [whole = '0', fraction = ''] = value.trim().split('.')
  const pence = Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
  return Number.isSafeInteger(pence) ? pence : null
}

/* ---------- personal settings (your GDV): one implementation in personalSettings.ts ---------- */
export { parsePersonalSettings, usePatchPersonalGdv, usePersonalAccess, usePersonalSettings } from './personalSettings'

/** Prefix of every area-potential query for one property (any experience). */
export function areaPotentialKey(propertyId: string) {
  return ['areaPotential', propertyId] as const
}
